<template>
  <article
    class="card card-product"
    :class="{ 'card-small': small }">
    <template v-if="!data">
      <div class="placeholder card-img-top" />

      <div class="card-body">
        <div class="placeholder pc-title" />
        <div class="placeholder pc-price" />
      </div>

      <div v-if="!simple" class="card-footer">
        <div><div class="placeholder pc-footer" /></div>
        <div><div class="placeholder pc-footer" /></div>
      </div>
    </template>

    <template v-else>
      <span v-if="data.new" class="badge-new">
        {{ $t('product.new') }}
      </span>

      <router-link :to="link" :title="data.name">
        <img v-lazy="getImageUrl(data.cover)"
          :title="data.name"
          :alt="data.name"
          class="card-img-top"
          loading="lazy">
      </router-link>

      <div class="card-body">
        <router-link :to="link" :title="data.name">
          <span class="product-category">{{ data.type_laptop }}</span>
          <div class="product-title">{{ data.name }}</div>
          <div class="product-price">{{ formatPrice(data.price_els) }}</div>
        </router-link>
      </div>

      <div v-if="!simple" class="card-footer">
        <div @click="addToCompare">
          <i class="far fa-fw fa-bring-forward"></i>
          {{ $t('general.compare') }}
        </div>
        <div :class="{ 'active': wishlistId }" @click="addToWishlist">
          <i v-show="!wishlistLoading" class="far fa-fw fa-heart"></i>
          <i v-show="wishlistLoading" class="far fa-spinner-third fa-spin"></i>

          <span v-if="!wishlistLoading">{{ $t('general.wishlist') }}</span>
          <span v-else>Loading</span>
        </div>
      </div>
    </template>
  </article>
</template>

<script>
import bus from '@/plugins/bus';
import find from 'lodash-es/find';
import CommonMixin from '@/mixins/Common';
import MemberMixin from '@/mixins/Member';

export default {
  name: 'CardProduct',

  mixins: [CommonMixin, MemberMixin],

  props: {
    data: {
      type: Object,
      required: false,
      default: () => {},
    },

    small: {
      type: Boolean,
      required: false,
      default: false,
    },

    simple: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      wishlistLoading: false,
    };
  },

  computed: {
    link() {
      return `/product/${this.data.slug}`;
    },

    params() {
      return {
        username: (!this.member) ? '' : this.member.email,
        product: this.data.slug,
      };
    },

    wishlistId() {
      if (!this.wishlists.result.length) return false;
      const res = find(this.wishlists.result, (item) => item.product.id === this.data.id);
      return (res) ? res.id : false;
    },
  },

  methods: {
    getImageUrl(image, size) {
      const imageSize = size || 'medium';
      return `${process.env.VUE_APP_IMAGE_PATH}thumbnail/${imageSize}/${image}`;
    },

    async addToCompare() {
      try {
        await this.$store.dispatch('addCompareItem', this.data);
        bus.$emit('toggle-compare-modal');
      } catch (e) {
        this.$toast.error('Failed to add compare', e);
      }
    },

    async addToWishlist() {
      if (!this.token) {
        this.$toast.error('You must login to add this to wishlist');
        return;
      }

      if (this.wishlistId) {
        try {
          this.wishlistLoading = true;
          await this.$mainApi.delete(`wishlist-products/${this.wishlistId}`);
          await this.$store.dispatch('user/getWishlists');
          this.$toast('Removed from wishlist');
          this.wishlistLoading = false;
        } catch (e) {
          this.$toast.error('Failed to remove wishlist');
        }
        return;
      }

      if (!this.wishlistId) {
        try {
          this.wishlistLoading = true;
          await this.$mainApi.post('wishlist-products', this.params);
          await this.$store.dispatch('user/getWishlists');
          this.$toast('Added to wishlist');
          this.wishlistLoading = false;
        } catch (e) {
          this.$toast.error('Failed to add wishlist');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .card-product {
    margin: 0 0.5rem 1rem;
    text-decoration: none;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 0.25rem 0.25rem -0.2rem rgba($black, 0.2);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 0.375rem 0.375rem -0.2rem rgba($black, 0.2);
    }

    &.card-small {
      .card-img-top {
        height: 10rem;
      }

      .card-footer {
        font-size: 0.625rem;
      }
    }

    .badge-new {
      position: absolute;
      top: -0.25rem;
      right: -1.75rem;
      padding: 1rem 0 0.25rem;
      width: 5rem;
      background-color: $color-primary;
      transform: rotate(45deg);
      z-index: 2;
      text-align: center;
      text-transform: uppercase;
      font-weight: 800;
      font-size: 0.625rem;
      color: rgba($black, 0.7);
    }

    .card-img-top {
      height: 12rem;
      object-fit: contain;
    }

    .card-body {
      margin-top: -0.5rem;
      padding: 0 0.75rem 0.75rem;
      z-index: 2;
      font-size: 0;
    }

    .card-footer {
      display: flex;
      justify-content: space-between;
      padding: 0;
      font-size: 0.625rem;

      @include media ("<tablet") {
        font-size: 0.625rem;
      }

      > div {
        padding: 0.5rem 0.75rem;
        width: 50%;
        text-align: center;
        text-overflow: ellipsis;
        text-transform: uppercase;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        color: $grey-medium;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background-color: $color-primary;
          color: $black;

          svg {
            color: $white;
          }
        }

        &:last-child {
          border-left: 1px solid rgba($grey, 0.4);
        }

        &.active {
          background-color: $color-primary;
          color: $white;
          font-weight: bold;

          svg {
            color: $white;
          }
        }

        svg {
          margin-right: 0.25rem;
          color: $grey;

          @include media ("<1200px") {
            display: none;
          }
        }
      }

      .wishlist-loading svg {
        color: $grey !important;
      }
    }

    .product-category {
      padding: 0.125rem 0.375rem;
      background-color: $black;
      border-radius: 0.25rem;
      color: $white;
      font-size: 0.6rem;
      font-weight: 500;
      text-transform: uppercase;
    }

    .product-title {
      margin-top: 0.5rem;
      height: 4rem;
      overflow: hidden;
      font-weight: 500;
      font-size: 0.75rem;
      color: $grey-dark;
      text-decoration: none;
    }

    .product-price {
      font-weight: bold;
      font-size: 0.875rem;
      color: $color-secondary;
    }

    .placeholder {
      @include placeholder-animation;
      height: 1rem;
      margin-bottom: 0.5rem;

      &.pc-title {
        margin-top: 1rem;
        width: 100%;
      }

      &.pc-price {
        width: 75%;
      }

      &.pc-footer {
        height: 0.75rem;
        margin: 0 auto;
        width: 100%;
      }
    }
  }
</style>
